import { useApi } from "@platform/api";
import {
  upsertAnalystNote,
  AnalystNotePayload,
  getAnalystNote,
} from "@platform/btx-plan-designer";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export type UseAnalystNoteResult = {
  content: string;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  upsertNoteAsync: (request: AnalystNotePayload) => Promise<void>;
  isUpserting: boolean;
  noteResponse?: AnalystNotePayload;
};

/**
 * Hook for fetching and upserting an analyst note for an RFP.
 * Operates on the note associated with the provided rfpId.
 */
export function useAnalystNote(rfpId: string): UseAnalystNoteResult {
  const queryClient = useQueryClient();
  const axiosInstance = useApi();
  const queryKey = ["analyst-note", rfpId];
  const query = useQuery<AnalystNotePayload, Error>({
    queryKey,
    queryFn: () => getAnalystNote(rfpId, axiosInstance),
    enabled: !!rfpId,
  });

  const mutation = useMutation({
    mutationFn: (request: AnalystNotePayload) =>
      upsertAnalystNote(rfpId, request, axiosInstance),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    content: typeof query.data?.content === "string" ? query.data.content : "",
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
    refetch: query.refetch,
    upsertNoteAsync: async (request: AnalystNotePayload) => {
      await mutation.mutateAsync(request);
    },
    isUpserting: mutation.isPending,
    noteResponse:
      query.data && typeof query.data === "object"
        ? (query.data as AnalystNotePayload)
        : undefined,
  };
}
